import { Box, Stack, styled, Tab, Typography } from '@mui/material';
import { format } from 'date-fns';
import { t } from 'i18next';
import { FC, SyntheticEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DatePicker } from '../../../../shared/components/datePicker/datePicker.component';
import { Switch as MuiSwitch } from '../../../../shared/components/form';
import { TabPanel, Tabs, TabsContainer } from '../../../../shared/components/tabs';
import { localeFormatterHelper } from '../../../../shared/helpers/formatter/localeFormatter.helper.ts';
import { companyConfigService } from '../../../../shared/services/companyConfig/companyConfig.service.ts';
import { Theme } from '../../../../shared/styles/theme/theme';
import {
  IDeliveryNotesTabDataRes,
  initPrintJobsDetailsState,
  IPrintJobsDetailsState,
  IProductionTabDataRes,
  printJobsDetailsState,
  PrintJobsTabs,
} from '../states/printJobsDetails.state';
import PrintDeliveryNotesTab from './printDeliveryNotes/printDeliveryNotes.tab';
import ProductionTab from './production/production.tab';

const PrintJobsDetails: FC<IPrintJobsDetailsProps> = ({ initPrintJobsDetailsData }) => {
  const params = useParams() as { tab: PrintJobsTabs };
  const navigate = useNavigate();
  const [isCADBAvailable, setIsCADBAvailable] = useState<boolean>(false);
  const [isBakery2bActivated, setIsBakery2bActivated] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<PrintJobsTabs>(params.tab);
  const [tabLoading, setTabLoading] = useState<boolean>(false);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [data, setData] = useState<IPrintJobsDetailsState>(
    // Lazy initialiser, and a deep copy of the fallback: handing the shared
    // module-level literal to `useState` would let this component's updates
    // reach every other consumer of it (WA-3769).
    () =>
      (initPrintJobsDetailsData as IPrintJobsDetailsState) ||
      structuredClone(initPrintJobsDetailsState),
  );
  const handleActiveTab: THandleActiveTab = (_, tab) => {
    setTabLoading(false);
    navigate(`/production/print-jobs/${tab}`);
  };

  useEffect(() => {
    const unsubPrintJobsDetailsState = printJobsDetailsState.sub.state().subscribe(setData);
    const unsubTabLoading = printJobsDetailsState.sub.tabLoading().subscribe(setTabLoading);
    const unsubListLoading = printJobsDetailsState.sub.listLoading().subscribe(setListLoading);
    const unsubCompanyConfig = companyConfigService.getConfigs().subscribe((config) => {
      const { isCADBAvailable, isBakery2bActivated } = config;
      setIsCADBAvailable(isCADBAvailable);
      setIsBakery2bActivated(isBakery2bActivated);
    });
    return () => {
      unsubPrintJobsDetailsState.unsubscribe();
      unsubTabLoading.unsubscribe;
      unsubListLoading.unsubscribe;
      unsubCompanyConfig.unsubscribe();
    };
  }, []);

  // Always tell the state which tab is selected — do NOT gate this on the
  // component's own `data` snapshot. `printJobs.page.tsx` publishes `pub.init`
  // during its render (WA-3778), so it lands before this component subscribes,
  // and `sub.state()` is `share()`d with no replay buffer: the emission is
  // gone by the time we attach, and the stream's own seed state is dropped by
  // `Sub`'s `action !== undefined` filter. `data` therefore keeps the loader
  // payload's `action: 'loader'` indefinitely, and the old
  // `data.action !== 'loader'` guard turned every tab click into a no-op —
  // nothing queried the opened tab, and `selectedTab` stayed on the tab the
  // loader had fetched, so a later date change queried that tab instead and
  // wiped the opened one's payload (WA-3819: "Print delivery notes" stayed
  // empty for dates that do have orders).
  //
  // Publishing unconditionally is cheap: `dataForSelectedTab` re-queries only
  // when the tab's payload is missing or stamped for another date
  // (`isTabDataFreshForDatePeriod`, WA-3769), so the loader's own fetch is
  // still not repeated on the initial mount.
  //
  // Keyed on `params.tab`, the only thing read here — not on the whole
  // `params` object. `useParams()` happens to hand back a reference that is
  // stable per navigation today, but now that the publish is unconditional a
  // future extra route param (or any change to that reference's stability)
  // would turn every re-render into another publish.
  useEffect(() => {
    printJobsDetailsState.pub.dataForSelectedTab(params.tab);
    setActiveTab(params.tab);
  }, [params.tab]);

  return (
    <>
      <Stack
        direction='row'
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <DatePicker
          defaultValue={localeFormatterHelper.localizedDate(data.datePeriod.fromDate)}
          onChange={(currentDate) => {
            const formattedDate = format(currentDate!, 'yyyy-MM-dd');
            printJobsDetailsState.pub.selectDatePeriod(formattedDate);
          }}
          navigation
          width={355}
          weekDay
        />
        <Box sx={{ display: 'flex', gap: '40px' }}>
          {isBakery2bActivated && (
            <SFlexContainer>
              <STypography>{t('production.orders_over_bakery2b')}</STypography>
              <MuiSwitch
                checked={data.statusList.bakery2b}
                size='small'
                label={data.statusList.bakery2b ? t('common.permitted') : t('common.forbidden')}
                onChange={(_, checked) => {
                  printJobsDetailsState.pub.changeStatusBakery2B(checked);
                }}
                color='primary'
                disabled={listLoading}
              />
            </SFlexContainer>
          )}
          {isCADBAvailable && (
            <SFlexContainer>
              <STypography>{t('production.orders_over_cashassist')}</STypography>
              <MuiSwitch
                checked={data.statusList.CAdirect}
                size='small'
                label={data.statusList.CAdirect ? t('common.permitted') : t('common.forbidden')}
                onChange={(_, checked) => {
                  printJobsDetailsState.pub.changeStatusCAdirect(checked);
                }}
                color='primary'
                disabled={listLoading}
              />
            </SFlexContainer>
          )}
        </Box>
      </Stack>
      <Tabs value={activeTab} onChange={handleActiveTab}>
        <Tab label={t('production.production')} value={PrintJobsTabs.production} />
        <Tab
          label={t('production.print_delivery_notes')}
          value={PrintJobsTabs.printDeliveryNotes}
        />
      </Tabs>
      <TabsContainer loading={tabLoading} sx={{ mt: 3 }}>
        <TabPanel value={PrintJobsTabs.production} activeValue={activeTab}>
          <ProductionTab
            data={data.dataTabs?.['production'] as IProductionTabDataRes}
            datePeriod={data.datePeriod}
            listLoading={listLoading}
          />
        </TabPanel>
        <TabPanel value={PrintJobsTabs.printDeliveryNotes} activeValue={activeTab}>
          <PrintDeliveryNotesTab
            data={data.dataTabs?.['print-delivery-notes'] as IDeliveryNotesTabDataRes}
            action={data.action}
            datePeriod={data.datePeriod}
            loading={listLoading}
          />
        </TabPanel>
      </TabsContainer>
    </>
  );
};
export default PrintJobsDetails;

const SFlexContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

const STypography = styled(Typography)({
  color: Theme.palette.secondary.light,
  fontFamily: Theme.typography.fontFamily,
  fontSize: '16px',
  marginBottom: '8px',
});

type THandleActiveTab = (evt: SyntheticEvent<Element, Event>, tab: PrintJobsTabs) => void;

interface IPrintJobsDetailsProps {
  initPrintJobsDetailsData?: IPrintJobsDetailsState;
}
